import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  Modal,
  Input,
  PrimaryButton,
  SecondaryButton,
  Typography,
  Spinner,
} from '@weareopenr/component-library';
import { getAddons } from 'src/api/getAddons/getAddons';
import { IAddonsResponse } from 'src/api/getAddons/types';
import { TAuthenticationType, ICreateExtensionRequest } from 'src/api/extensions';
import { useCreateExtension } from 'src/hooks/useExtensions';
import { QueryKeys } from 'src/constants/QueryKeys';
import {
  buildAuthenticationPayload,
  IAuthFormValues,
} from 'src/utils/buildAuthenticationPayload/buildAuthenticationPayload';
import { Logo } from '../Logo/Logo';
import { getLogoForAddon } from 'src/utils/getLogo';
import styles from './ConnectExtensionModal.module.scss';

interface IConnectExtensionModalProps {
  open: boolean;
  onClose: () => void;
  brandId: string;
  existingAddonIds: string[];
}

export const ConnectExtensionModal = ({
  open,
  onClose,
  brandId,
  existingAddonIds,
}: IConnectExtensionModalProps) => {
  const [selectedAddon, setSelectedAddon] = useState<IAddonsResponse | null>(null);
  const [selectedAuthType, setSelectedAuthType] = useState<TAuthenticationType | null>(null);

  const {
    data: addons,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [QueryKeys.ADDONS],
    queryFn: getAddons,
    enabled: open,
  });

  const createMutation = useCreateExtension(() => {
    handleClose();
  });

  const { control, handleSubmit, reset } = useForm<IAuthFormValues>({
    defaultValues: {
      username: '',
      password: '',
      access_token: '',
      contents: '',
    },
  });

  const handleClose = () => {
    setSelectedAddon(null);
    setSelectedAuthType(null);
    reset();
    onClose();
  };

  const handleAddonSelect = (addon: IAddonsResponse) => {
    setSelectedAddon(addon);
    // Auth type is defined by the addon, not user-selectable
    const authType = addon.authentication_types?.[0] as TAuthenticationType | undefined;
    setSelectedAuthType(authType || null);
  };

  const onSubmit = (data: IAuthFormValues) => {
    if (!selectedAddon) return;

    const payload: ICreateExtensionRequest = {
      addon_id: selectedAddon.addon_id,
    };

    const authentication = buildAuthenticationPayload(selectedAuthType ?? undefined, data);
    if (authentication) {
      payload.authentication = authentication;
    }

    createMutation.mutate({ brandId, payload });
  };

  return (
    <Modal closeModal={handleClose} open={open}>
      <div className={styles['modal-content']}>
        <Typography size="xxlarge" weight="semi-bold" className={styles['heading']}>
          Connect Extension
        </Typography>

        {open && isLoading && (
          <div className={styles['loading']}>
            <Spinner size="large" colour="rainbow" />
          </div>
        )}

        {open && isError && !isLoading && (
          <Typography className={styles['description']}>
            Failed to load addons. Please try again.
          </Typography>
        )}

        {open && !isLoading && !isError && !selectedAddon && addons && (
          <>
            <Typography className={styles['description']}>Select an addon to connect</Typography>
            <div className={styles['addon-grid']}>
              {addons
                .filter((addon) => !existingAddonIds.includes(addon.addon_id))
                .map((addon) => (
                  <button
                    key={addon.addon_id}
                    type="button"
                    className={styles['addon-card']}
                    onClick={() => handleAddonSelect(addon)}
                  >
                    <Logo
                      src={getLogoForAddon(addon.addon_name)}
                      name={addon.addon_name}
                      className={styles['addon-logo']}
                    />
                    <div className={styles['addon-info']}>
                      <Typography weight="semi-bold">{addon.addon_name}</Typography>
                      <Typography>{addon.publisher_name}</Typography>
                    </div>
                  </button>
                ))}
            </div>
          </>
        )}

        {open && !isLoading && selectedAddon && (
          <form id="connect-form" onSubmit={handleSubmit(onSubmit)}>
            <div className={styles['selected-addon']}>
              <Logo
                src={getLogoForAddon(selectedAddon.addon_name)}
                name={selectedAddon.addon_name}
                className={styles['addon-logo']}
              />
              <div>
                <Typography weight="semi-bold">{selectedAddon.addon_name}</Typography>
                <button
                  type="button"
                  className={styles['change-link']}
                  onClick={() => {
                    setSelectedAddon(null);
                    setSelectedAuthType(null);
                    reset();
                  }}
                >
                  Change
                </button>
              </div>
            </div>

            {selectedAuthType === 'BASIC' && (
              <>
                <div className={styles['form-group']}>
                  <Controller
                    name="username"
                    control={control}
                    rules={{ required: 'Username is required' }}
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        id={field.name}
                        type="text"
                        label="Username"
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles['form-group']}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: 'Password is required' }}
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        id={field.name}
                        type="password"
                        label="Password"
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
              </>
            )}

            {selectedAuthType === 'TOKEN' && (
              <div className={styles['form-group']}>
                <Controller
                  name="access_token"
                  control={control}
                  rules={{
                    required: 'Access token is required',
                    minLength: { value: 8, message: 'Access token must be at least 8 characters' },
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id={field.name}
                      type="text"
                      label="Access Token"
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            )}

            {selectedAuthType === 'CERTIFICATE' && (
              <div className={styles['form-group']}>
                <Controller
                  name="contents"
                  control={control}
                  rules={{ required: 'Certificate contents is required' }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id={field.name}
                      type="text"
                      label="Certificate Contents"
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            )}

            {!selectedAddon.authentication_types?.length && (
              <Typography className={styles['no-auth-message']}>
                This addon does not require authentication.
              </Typography>
            )}
          </form>
        )}

        <div className={styles['controls']}>
          <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
          {selectedAddon && (
            <PrimaryButton type="submit" form="connect-form" loading={createMutation.isPending}>
              Connect
            </PrimaryButton>
          )}
        </div>
      </div>
    </Modal>
  );
};
